import * as yup from "yup";
import {
  getStringRequiredFieldMessage,
  validateGlobalPhoneNumber,
} from "@shared/utils/yup-extensions";

type FieldTypes = "email" | "phone" | "text";

const fieldsSchema = yup.object().shape({
  text: yup.string().trim().max(100).label("Text"),
  phone: yup
    .string()
    .trim()
    .test(
      validateGlobalPhoneNumber({
        message: "Invalid phone number",
        optional: true,
      }),
    )
    .nullable()
    .label("Phone Number"),
  email: yup
    .string()
    .email()
    .trim()
    .min(1, getStringRequiredFieldMessage)
    .required()
    .label("Email"),
});

export const validateField = async (
  type: FieldTypes,
  value: any,
): Promise<string | null> => {
  try {
    await fieldsSchema.validateAt(type, {
      [type]: value,
    });
    return null;
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      return error.message;
    }
    return "Invalid value";
  }
};

/**
 * Type-specific formatters for display
 */
type Formatter = (value: string) => string;

const formatters: Record<string, Formatter> = {
  phone: (value: string) => {
    if (!value) return "";
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return value;
  },
  email: (value: string) => value,
  text: (value: string) => value,
};

/**
 * Format value based on field type
 * Allows easy extension for new types without changing component logic
 */
export const formatValueByType = (type: string, value: string): string => {
  if (!value) return "";
  const formatter = formatters[type];
  return formatter ? formatter(value) : value;
};

/**
 * Extract numeric phone number from formatted input
 * e.g., "(314) 356-7890" -> "3143567890"
 */
export const extractPhoneNumeric = (value: string): string => {
  if (!value) return "";
  return value.replace(/\D/g, "");
};
